// src\services\ogImageService.js
/**
 * OG Image Service - FRONTEND ONLY
 * This service is ONLY for the share modal UI preview.
 * Social media scrapers get OG tags from server-side API endpoints.
 *
 * DO NOT use this for generating OG meta tags in Helmet.
 */

const SITE_URL = 'https://wdhejbul.com';
const DEFAULT_IMAGE = '/og-images/default/og-image-default.png';

/**
 * Get preview data for share modal
 * Returns ONLY what's needed for the modal UI
 */
export const ogImageService = {
  // ============================================
  // PORTFOLIO - For share modal preview
  // ============================================
  portfolioProject: project => {
    if (!project) return getDefaultData();

    const image =
      project.thumbnailUrl || project.images?.[0]?.url || DEFAULT_IMAGE;

    return {
      title: project.seoTitle || project.title || 'Portfolio Project',
      description: project.seoDescription || project.description || '',
      image: image,
      url: `${SITE_URL}/portfolio/${project.id}`,
    };
  },

  // ============================================
  // BLOG - For share modal preview
  // ============================================
  blogPost: blog => {
    if (!blog) return getDefaultData();

    const image = blog.ogImage || blog.featuredImage || DEFAULT_IMAGE;

    return {
      title: blog.seoTitle || blog.title || 'Blog Post',
      description: blog.seoDescription || blog.excerpt || '',
      image: image,
      url: `${SITE_URL}/blog/${blog.slug}`,
    };
  },

  blogHome: () =>
    getPageData(
      'Blog | WD Hejbul - Insights & Stories',
      'Explore insights, stories, and expertise from WD Hejbul on design, development, and creativity.',
      '/og-images/blog/og-image-blog-category.png',
    ),

  // ============================================
  // SERVICES - For share modal preview
  // ============================================
  service: service => {
    if (!service) return getDefaultData();

    return {
      title: service.name || 'Service',
      description: service.description || '',
      image: service.imageUrl || DEFAULT_IMAGE,
      url: `${SITE_URL}/service/${service.id}`,
    };
  },

  servicesHome: () =>
    getPageData(
      'Services | WD Hejbul - Design & Branding',
      'Professional logo design, vector tracing, and product visuals.',
      '/og-images/services/og-image-services-default.png',
    ),

  // ============================================
  // SUBSCRIPTION - For share modal preview
  // ============================================
  subscription: subscription => {
    if (!subscription) return getDefaultData();

    return {
      title: subscription.name || subscription.title || 'Subscription Plan',
      description:
        subscription.description || 'Professional design subscription service',
      image:
        subscription.imageUrl ||
        '/og-images/subscription/og-image-subscription-default.png',
      url: `${SITE_URL}/subscription/${subscription.id}`,
    };
  },

  subscriptionHome: () =>
    getPageData(
      'Subscription Plans | WD Hejbul - Design Services',
      'Explore flexible subscription plans for professional design services.',
      '/og-images/pages/og-image-subscription.png',
    ),

  // ============================================
  // STATIC PAGES - For share modal preview
  // ============================================
  home: () =>
    getPageData(
      'WD Hejbul | Brand & Product Visual Designer',
      'Professional logo design, vector tracing, and product visuals crafted for modern brands.',
      '/og-images/pages/og-image-home.png',
    ),

  about: () =>
    getPageData(
      'About | WD Hejbul - Brand & Product Visual Designer',
      'Learn about WD Hejbul, a creative designer specializing in brand identity and product visuals.',
      '/og-images/pages/og-image-about.png',
    ),

  contact: () =>
    getPageData(
      'Contact | WD Hejbul - Get in Touch',
      "Let's discuss your next project. Contact WD Hejbul for logo design, branding, and product visuals.",
      '/og-images/pages/og-image-contact.png',
    ),

  hire: () =>
    getPageData(
      'Hire Me | WD Hejbul - Available for Projects',
      'Looking for a professional designer? Hire WD Hajbul for your next branding or design project.',
      '/og-images/pages/og-image-hire.png',
    ),

  work: () =>
    getPageData(
      'Work | WD Hejbul - Design Portfolio',
      'Explore my design work and see how I can help your brand stand out.',
      '/og-images/pages/og-image-work.png',
    ),

  profile: () =>
    getPageData(
      'WD Hejbul - Professional Designer Profile',
      'View the professional profile of WD Hejbul, a creative designer specializing in brand identity and product visuals.',
      '/og-images/pages/og-image-profile.png',
    ),

  // ============================================
  // DEFAULT FALLBACK
  // ============================================
  default: () => getDefaultData(),

  // ============================================
  // PORTFOLIO HOME (alias for work)
  // ============================================
  portfolioHome: () =>
    getPageData(
      'Portfolio | WD Hejbul - Design Work',
      'Explore my portfolio of logo design, branding, and product visuals.',
      '/og-images/portfolio/og-image-portfolio-default.png',
    ),
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function getDefaultData() {
  return {
    title: 'WD Hejbul | Brand & Product Visual Designer',
    description:
      'Professional logo design, vector tracing, and product visuals crafted for modern brands.',
    image: DEFAULT_IMAGE,
    url: SITE_URL,
  };
}

function getPageData(title, description, image) {
  return {
    title: title,
    description: description,
    image: image || DEFAULT_IMAGE,
    url: SITE_URL,
  };
}

export default ogImageService;
